/*  iceberg-ui  */
import {
    Box,
    Button,
    Card,
    Flex,
    Flyout,
    ModalFooter
} from '@auviknetworks/iceberg-ui';

/* MUI */
import { Typography } from '@mui/material';

/* Helpers */
import {
    getReportConfigByType,
    getSelectedAdvancedOptions,
    getSelectedSubReportOptions
} from 'application/Home/Reports/helpers';

/* Shared */
import {
    AppPicker,
    TagsPicker,
    UserPicker
} from 'application/Shared/Components/Pickers';
import { FontSizes, Z_INDEX } from 'application/Shared/Constants';
import { formatNextDeliveryTime } from 'application/Shared/Helpers/scheduledReportsDeliveryTime';
import { getClientName } from 'application/Shared/Helpers/tenant';
import { ScheduledReport as ScheduledReportType } from 'application/Shared/types/scheduledReport';

/*  NPM  */
import { format } from 'date-fns';

/*  Hooks  */
import { useSession } from 'hooks/redux/Selectors/session';

/*  React  */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

interface InfoCardProps {
    title: string;
    sections: Array<{
        label?: string;
        value: string | React.ReactNode;
    }>;
}

/**
 * A reusable card component to display report configuration.
 *
 * This component creates a card with a title and multiple sections of information.
 * Each section can have an optional label and a value that can be either text or React components.
 *
 * @example
 * ```tsx
 * <InfoCard
 *   title="Report Details"
 *   sections={[
 *     { label: "Name", value: "Monthly Usage Report" },
 *     { label: "Status", value: "Active" },
 *     { value: "This report runs every month" }
 *   ]}
 * />
 * ```
 */
const InfoCard = ({ title, sections }: InfoCardProps) => (
    <Card variant="outlined" padding="16px">
        <Typography
            color="textSecondary"
            fontWeight={600}
            style={{ fontSize: FontSizes[2] }}
        >
            {title}
        </Typography>
        <hr />
        {sections.map((section, index) => (
            <Box
                key={section.label || `section-${index}`}
                sx={{ marginBottom: '16px' }}
            >
                {section.label && (
                    <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{
                            marginBottom: '8px'
                        }}
                    >
                        {section.label}
                    </Typography>
                )}
                {section.value}
            </Box>
        ))}
    </Card>
);

/**
 * Displays a client's name by searching through partner tenant links from the session.
 * Shows the client ID if the name cannot be found.
 *
 * @param clientId - The client's unique identifier
 * @returns Client name or client ID as fallback
 */
const ClientName = ({ clientId }: { clientId: string }) => {
    const session = useSession();
    const clientName = getClientName({ clientId, session });

    if (!clientName) {
        return (
            <Typography variant="body2" color="textSecondary">
                {clientId}
            </Typography>
        );
    }

    return <Typography variant="body1">{clientName}</Typography>;
};

interface ViewScheduledReportDetailsProps {
    onClose: () => void;
    scheduledReport: ScheduledReportType | null;
}

/**
 * Modal component that displays the complete configuration of a scheduled report in a read-only format.
 *
 * This component shows all the selected options and settings for a scheduled report including:
 * - Report details (name, next delivery time)
 * - Selected time period
 * - Applications to report on
 * - Users to include in the report
 * - Client selections
 * - Tag filters
 * - Sub-report options
 * - Advanced report settings
 *
 * The component uses InfoCard components to organize the information into clear sections.
 * Users can view all settings but cannot edit them from this modal.
 */
export const ViewScheduledReportSetup = ({
    onClose,
    scheduledReport
}: ViewScheduledReportDetailsProps) => {
    const { t } = useTranslation();
    const { partnerId } = useParams();
    const session = useSession();

    if (!scheduledReport) {
        return null;
    }

    const { dateCreated, params } = scheduledReport;
    const {
        applicationSelection,
        clientSelection,
        discoveryOverviewMatrixTags,
        endTime,
        period,
        platformTenantId,
        reportSelections,
        startTime,
        tagSelection,
        type,
        userSelection
    } = params || {};

    const reportConfig = getReportConfigByType(type);
    const advancedOptions = reportConfig
        ? getSelectedAdvancedOptions({ params, reportConfig, t })
        : [];
    const tagValues =
        discoveryOverviewMatrixTags[0] !== '0'
            ? discoveryOverviewMatrixTags
            : tagSelection;

    const clientName = getClientName({
        clientId: platformTenantId || partnerId,
        session
    });

    return (
        <Flyout
            onClose={onClose}
            title={t('Reports.viewScheduledReportSetup')}
            headerIcon="View"
            zIndex={Z_INDEX.FLYOUT_LOW}
        >
            <Flex
                display="flex"
                flexDirection="column"
                height="100%"
                justifyContent="space-between"
            >
                <Flex
                    sx={{
                        flexDirection: 'column',
                        gap: '24px',
                        overflow: 'auto',
                        padding: '24px'
                    }}
                >
                    <Typography variant="body1">
                        {t('Reports.createNewReportToEdit')}
                    </Typography>
                    {/* report details */}
                    <InfoCard
                        title={t('Reports.reportDetails')}
                        sections={[
                            {
                                label: t('Report.name'),
                                value: scheduledReport.name
                            },
                            {
                                label: t('Reports.tenantName'),
                                value: clientName
                            },
                            {
                                label: t('Reports.reportType'),
                                value: t(`Reports.Title.${type}.report`)
                            },
                            {
                                label: t('Reports.nextDeliveryTime'),
                                value: formatNextDeliveryTime({
                                    dateCreated,
                                    period
                                })
                            }
                        ]}
                    />
                    {/* report period */}
                    <InfoCard
                        title={t('Reports.selectedPeriod')}
                        sections={[
                            {
                                label: t('Report.period', {
                                    period: t(`Reports.period.${period}`)
                                }),
                                value:
                                    startTime &&
                                    endTime &&
                                    `${format(
                                        new Date(startTime),
                                        'yyyy/MM/dd'
                                    )} - ${format(
                                        new Date(endTime),
                                        'yyyy/MM/dd'
                                    )}`
                            }
                        ]}
                    />
                    {/* Applications to report on */}
                    {applicationSelection &&
                        applicationSelection.length > 0 && (
                            <InfoCard
                                title={t('Reports.applicationsToReportOn')}
                                sections={[
                                    {
                                        label: t(
                                            'Reports.selectedApplications'
                                        ),
                                        value: (
                                            <Box mt="8px">
                                                {applicationSelection[0] ===
                                                '0' ? (
                                                    <Typography variant="body1">
                                                        {t(
                                                            'Reports.applications.noApplicationsSelected'
                                                        )}
                                                    </Typography>
                                                ) : (
                                                    applicationSelection.map(
                                                        (appId) => (
                                                            <Box
                                                                key={appId}
                                                                mb="8px"
                                                                sx={{
                                                                    '& img': {
                                                                        marginRight:
                                                                            '8px'
                                                                    }
                                                                }}
                                                            >
                                                                <AppPicker
                                                                    allApplications
                                                                    multiple
                                                                    allValue={t(
                                                                        'Reports.applications.allApplicationsSelected'
                                                                    )}
                                                                    hideManagedBadge
                                                                    allValueId="0"
                                                                    value={[
                                                                        appId
                                                                    ]}
                                                                    visualOnly
                                                                />
                                                            </Box>
                                                        )
                                                    )
                                                )}
                                            </Box>
                                        )
                                    }
                                ]}
                            />
                        )}
                    {/* Users Selected */}
                    {reportConfig?.setup.userSelectionOptions &&
                        userSelection &&
                        userSelection.length > 0 && (
                            <InfoCard
                                title={t('Reports.usersToReportOn')}
                                sections={[
                                    {
                                        label: t('Reports.selectedUsers'),
                                        value: (
                                            <Box mt="8px">
                                                {userSelection.map((userId) => (
                                                    <Box
                                                        key={userId}
                                                        sx={{
                                                            marginBottom: '8px'
                                                        }}
                                                    >
                                                        <UserPicker
                                                            multiple={false}
                                                            allValue={t(
                                                                'Reports.users.allUsersSelected'
                                                            )}
                                                            allValueId="0"
                                                            nameOnly
                                                            visualOnly
                                                            value={[userId]}
                                                        />
                                                    </Box>
                                                ))}
                                            </Box>
                                        )
                                    }
                                ]}
                            />
                        )}
                    {/* Clients Selected */}
                    {reportConfig?.setup.clientSelection &&
                        clientSelection &&
                        clientSelection[0] !== '0' && (
                            <InfoCard
                                title={t('Reports.clientsToReportOn')}
                                sections={[
                                    {
                                        label: t('Reports.selectedClients'),
                                        value: (
                                            <Box mt="8px">
                                                {clientSelection.map(
                                                    (clientId) => (
                                                        <Box
                                                            key={clientId}
                                                            sx={{
                                                                marginBottom:
                                                                    '8px'
                                                            }}
                                                        >
                                                            <ClientName
                                                                clientId={
                                                                    clientId
                                                                }
                                                            />
                                                        </Box>
                                                    )
                                                )}
                                            </Box>
                                        )
                                    }
                                ]}
                            />
                        )}
                    {/* Tags Selected */}
                    {(reportConfig?.setup.tagSelection ||
                        reportConfig?.setup.multiAppSelectionOptions
                            ?.tagSelection) && (
                        <InfoCard
                            title={t('Common.tags')}
                            sections={[
                                {
                                    label: t('Reports.selectedTags'),
                                    value:
                                        tagValues[0] === '0' ? (
                                            t('Reports.tags.noTagsSelected')
                                        ) : (
                                            <TagsPicker
                                                disableAllOption={false}
                                                value={tagValues}
                                                nameOnly
                                                visualOnly
                                                allValue={t(
                                                    'Reports.tags.allTagsSelected'
                                                )}
                                            />
                                        )
                                }
                            ]}
                        />
                    )}
                    {/* Sub-report options */}
                    {reportConfig?.setup.subReportSelection &&
                        reportSelections &&
                        reportSelections.length > 0 && (
                            <InfoCard
                                title={t('Reports.subReportOptions')}
                                sections={getSelectedSubReportOptions({
                                    reportSelections,
                                    t,
                                    type
                                }).map((group) => ({
                                    label: group.type,
                                    value: (
                                        <Box>
                                            {group.options.map((option) => (
                                                <Box
                                                    key={option}
                                                    sx={{
                                                        marginBottom: '8px'
                                                    }}
                                                >
                                                    <Typography variant="body1">
                                                        {option}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    )
                                }))}
                            />
                        )}
                    {/* Advanced report options */}
                    {reportConfig?.setup.advancedReportSelectionOptions &&
                        advancedOptions.length > 0 && (
                            <InfoCard
                                title={t('Reports.advanced_setup')}
                                sections={[
                                    {
                                        value: (
                                            <Box>
                                                {advancedOptions.map(
                                                    (option) => (
                                                        <Box
                                                            key={option}
                                                            sx={{
                                                                marginBottom:
                                                                    '8px'
                                                            }}
                                                        >
                                                            <Typography variant="body1">
                                                                {option}
                                                            </Typography>
                                                        </Box>
                                                    )
                                                )}
                                            </Box>
                                        )
                                    }
                                ]}
                            />
                        )}
                </Flex>
                <ModalFooter sx={{ justifyContent: 'flex-start' }}>
                    <Button variant="secondary" onClick={onClose}>
                        {t('Common.cancel')}
                    </Button>
                </ModalFooter>
            </Flex>
        </Flyout>
    );
};
